import axios from 'axios'

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8089'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 seconds for heavier queries
  headers: {
    'Content-Type': 'application/json',
  },
})

// Build a query string from a filters object, dropping empty values.
// The inline form of this is repeated ~48x across this file for historical
// reasons; new methods should use this helper (migrating the existing call
// sites is a separate cleanup, not something to fold into a feature PR).
const buildQuery = (filters = {}) => {
  const params = new URLSearchParams()
  Object.entries(filters || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.append(k, v)
  })
  return params.toString()
}

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      console.error('🔒 401 Unauthorized - logging out')
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    } else if (error.response?.status === 403) {
      // Log forbidden access for debugging
      console.error('🚫 403 Forbidden:', {
        url: error.config?.url,
        method: error.config?.method,
        detail: error.response?.data?.detail,
        user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null
      })
    }
    return Promise.reject(error)
  }
)

// API endpoints
export const apiService = {
  // Simple in-flight de-duplication map (URL -> Promise)
  _inflight: new Map(),
  _dedupGet(url) {
    if (this._inflight.has(url)) {
      return this._inflight.get(url)
    }
    const promise = api.get(url).finally(() => {
      // Clean up after completion to allow future requests
      this._inflight.delete(url)
    })
    this._inflight.set(url, promise)
    return promise
  },
  
  // Authentication endpoints
  login: (formData) => api.post('/auth/token', formData, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
  }),
  
  logout: async () => {
    try {
      await api.post('/auth/logout')
    } finally {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
    }
  },
  
  getCurrentUser: () => api.get('/auth/me'),
  
  // OAuth endpoints
  getGoogleAuthUrl: () => api.get('/oauth/google/authorize'),
  googleCallback: (code, state) => api.post('/oauth/google/callback', { code, state }),
  
  refreshToken: () => api.post('/auth/refresh'),
  
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  }),
  
  // Admin user management endpoints
  getUsers: (params = {}) => api.get('/auth/users', { params }),
  
  createUser: (userData) => api.post('/auth/users', userData),
  
  getUser: (userId) => api.get(`/auth/users/${userId}`),
  
  updateUser: (userId, userData) => api.put(`/auth/users/${userId}`, userData),
  
  deleteUser: (userId) => api.delete(`/auth/users/${userId}`), // Soft delete (deactivate)
  
  activateUser: (userId) => api.post(`/auth/users/${userId}/activate`), // Reactivate user
  
  permanentlyDeleteUser: (userId) => api.post(`/auth/users/${userId}/permanent-delete`), // Hard delete (permanent)
  
  resetUserPassword: (userId, newPassword) => api.post(`/auth/users/${userId}/reset-password`, { new_password: newPassword }),
  
  getAuditLog: (params = {}) => api.get('/auth/audit-log', { params }),
  
  cleanupSessions: () => api.post('/auth/cleanup-sessions'),
  
  // Dashboard endpoints
  getDashboardSummary: (params = {}) => api.get('/dashboard/summary', { params }),
  getOperatorSummaries: (params = {}) => api.get('/dashboard/summary/by-operator', { params: { coverage_days: 15, breakdown: true, ...params } }),
  
  // Sales trends - v2 refactored endpoints
  getSalesTrends: (operator = 'ORANGE', params = {}) => {
    const searchParams = new URLSearchParams({ operator })
    if (params.month !== undefined && params.month !== null) searchParams.append('month', params.month.toString())
    if (params.year !== undefined && params.year !== null) searchParams.append('year', params.year.toString())
    return api.get(`/v2/sales/trend?${searchParams.toString()}`)
  },
  getAllSalesTrends: (params = {}) => Promise.all([
    apiService.getSalesTrends('ORANGE', params),
    apiService.getSalesTrends('TMOBILE', params),
    apiService.getSalesTrends('PLUS', params),
    apiService.getSalesTrends('P4', params)
  ]),
  
  // Sales goals - v2 refactored endpoints
  getSalesGoalsV2: (params = {}) => {
    const searchParams = new URLSearchParams()
    if (params.month !== undefined && params.month !== null) searchParams.append('month', params.month.toString())
    if (params.year !== undefined && params.year !== null) searchParams.append('year', params.year.toString())
    const queryString = searchParams.toString()
    return api.get(`/v2/sales/goals${queryString ? '?' + queryString : ''}`)
  },
  
  // Daily sales per operator (for Overview chart)
  getDailySales: (days = 14) => api.get('/v2/sales/daily', { params: { days } }),

  // Legacy endpoints (keeping for backward compatibility)
  getDashboardData: () => api.get('/dashboard'),
  getOperatorStatus: () => api.get('/operator-status'),
  getSalesGoals: () => api.get('/sales-goals'),
  
  // NBV recommendations
  getNBVRecommendations: () => api.get('/nbv/recommendations'),
  
  // Visit effectiveness
  getVisitEffectiveness: (params = {}) => {
    console.log('🔍 API Service getVisitEffectiveness called with params:', params)
    
    // Handle visit_type array parameter properly
    if (params.visit_type && Array.isArray(params.visit_type)) {
      const visitTypes = params.visit_type
      const otherParams = { ...params }
      delete otherParams.visit_type
      
      // Create URLSearchParams to handle multiple visit_type parameters
      const searchParams = new URLSearchParams()
      Object.entries(otherParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          searchParams.append(key, value)
        }
      })
      visitTypes.forEach(type => searchParams.append('visit_type', type))
      
      const url = `/visit/effectiveness?${searchParams.toString()}`
      console.log('🔍 API Service calling URL:', url)
      
      return api.get(url)
    }
    
    console.log('🔍 API Service calling /visit/effectiveness with params:', params)
    return api.get('/visit/effectiveness', { params })
  },
  getVisitEffectivenessSummary: (params = {}) => {
    // Handle visit_type array parameter properly
    if (params.visit_type && Array.isArray(params.visit_type)) {
      const visitTypes = params.visit_type
      const otherParams = { ...params }
      delete otherParams.visit_type
      
      // Create URLSearchParams to handle multiple visit_type parameters
      const searchParams = new URLSearchParams()
      Object.entries(otherParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          searchParams.append(key, value)
        }
      })
      visitTypes.forEach(type => searchParams.append('visit_type', type))
      
      return api.get(`/visit/effectiveness/summary?${searchParams.toString()}`)
    }
    return api.get('/visit/effectiveness/summary', { params })
  },
  getVisitOperators: () => api.get('/visit/operators'),
  getVisitedSalonsSales: (params = {}) => api.get('/visit/visited-salons-sales', { params }),
  getVisitDetails: (visitId) => api.get(`/visit/details/${visitId}`),

  // Network coverage
  getNetworkCoverage: (params = {}) => api.get('/network/coverage', { params }),
  
  // KAM performance
  getKAMPerformance: (params = {}) => {
    const searchParams = new URLSearchParams()
    if (params.month !== undefined && params.month !== null) searchParams.append('month', params.month.toString())
    if (params.year !== undefined && params.year !== null) searchParams.append('year', params.year.toString())
    if (params.product_group) searchParams.append('product_group', params.product_group)

    const queryString = searchParams.toString()
    return api.get(`/v2/kam/performance${queryString ? '?' + queryString : ''}`)
  },
  exportKAMPerformance: async (params = {}) => {
    // Export KAM performance to XLSX
    const searchParams = new URLSearchParams()
    if (params.month !== undefined && params.month !== null) searchParams.append('month', params.month.toString())
    if (params.year !== undefined && params.year !== null) searchParams.append('year', params.year.toString())
    
    const queryString = searchParams.toString()
    const token = localStorage.getItem('auth_token')
    const headers = {
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${API_BASE_URL}/v2/kam/performance/export${queryString ? '?' + queryString : ''}`, {
      method: 'GET',
      headers
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `Performance_KAM_${new Date().toISOString().slice(0, 10)}.xlsx`
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename=(.+)/)
      if (filenameMatch) {
        filename = filenameMatch[1].replace(/"/g, '')
      }
    }
    
    const blob = await response.blob()
    return { blob, filename }
  },
  getKAMGoalsV2: () => api.get('/v2/kam/goals/summary'), // Real KAM goals per operator from providers
  
  // Individual KAM performance comparison
  getKAMPerformanceIndividual: (params = {}) => {
    // params: kam_name, operator_code, start_month, start_year, end_month, end_year
    const searchParams = new URLSearchParams()
    if (params.kam_name !== undefined && params.kam_name !== null) searchParams.append('kam_name', params.kam_name)
    if (params.operator_code !== undefined && params.operator_code !== null) searchParams.append('operator_code', params.operator_code)
    if (params.start_month !== undefined && params.start_month !== null) searchParams.append('start_month', params.start_month.toString())
    if (params.start_year !== undefined && params.start_year !== null) searchParams.append('start_year', params.start_year.toString())
    if (params.end_month !== undefined && params.end_month !== null) searchParams.append('end_month', params.end_month.toString())
    if (params.end_year !== undefined && params.end_year !== null) searchParams.append('end_year', params.end_year.toString())
    
    const queryString = searchParams.toString()
    // 10-minute timeout for heavy yearly queries
    return api.get(`/v2/kam/performance/individual${queryString ? '?' + queryString : ''}`, { timeout: 600000 })
  },
  exportKAMPerformanceIndividual: async (params = {}) => {
    // Export individual KAM performance to XLSX
    const searchParams = new URLSearchParams()
    if (params.kam_name !== undefined && params.kam_name !== null) searchParams.append('kam_name', params.kam_name)
    if (params.operator_code !== undefined && params.operator_code !== null) searchParams.append('operator_code', params.operator_code)
    if (params.start_month !== undefined && params.start_month !== null) searchParams.append('start_month', params.start_month.toString())
    if (params.start_year !== undefined && params.start_year !== null) searchParams.append('start_year', params.start_year.toString())
    if (params.end_month !== undefined && params.end_month !== null) searchParams.append('end_month', params.end_month.toString())
    if (params.end_year !== undefined && params.end_year !== null) searchParams.append('end_year', params.end_year.toString())
    
    const queryString = searchParams.toString()
    const token = localStorage.getItem('auth_token')
    const headers = {
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    
    // Create abort controller with 10-minute timeout for heavy yearly queries
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 600000) // 10 minutes
    
    const response = await fetch(`${API_BASE_URL}/v2/kam/performance/individual/export${queryString ? '?' + queryString : ''}`, {
      method: 'GET',
      headers,
      signal: controller.signal
    })
    
    clearTimeout(timeoutId)
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `Performance_KAM_Individual_${new Date().toISOString().slice(0, 10)}.xlsx`
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename=(.+?)(?:;|$)/)
      if (filenameMatch) {
        filename = filenameMatch[1].replace(/"/g, '')
      }
    }
    
    const blob = await response.blob()
    return { blob, filename }
  },
  
  // Analytics
  getAnomalies: () => api.get('/analytics/anomalies'),
  getHeatmap: () => api.get('/analytics/heatmap'),
  
  // AI analysis
  getAIAnalysis: () => api.get('/ai-analysis'),
  
  
  // Operators
  getOperators: () => api.get('/operators'),
  
  // Data freshness - get last update date for each operator's sales data
  getDataFreshness: () => api.get('/operators/data-freshness'),
  
  // Salon availability details
  getSalonAvailabilityDetails: (operatorCode, plantCode, days = 30) => 
    api.get(`/operators/${operatorCode}/salon/${plantCode}/availability-details`, { params: { days } }),

  // Salon availability details per-product (for PLK)
  getSalonAvailabilityDetailsPerProduct: (operatorCode, plantCode, days = 30) => 
    api.get(`/operators/${operatorCode}/salon/${plantCode}/availability-details-per-product`, { params: { days } }),

  // Export stock overview to XLSX
  exportStockOverview: async (operatorCode, specialOnly = true) => {
    const token = localStorage.getItem('auth_token')
    const headers = {
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    
    const response = await fetch(`${API_BASE_URL}/operators/${operatorCode}/stock-overview/export?special_only=${specialOnly}`, {
      method: 'GET',
      headers
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `zatowarowanie_${operatorCode.toLowerCase()}_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.xlsx`
    
    if (contentDisposition) {
      // Match filename="value" or filename=value (stopping at semicolon or end)
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/)
      if (filenameMatch) {
        filename = filenameMatch[1]
      }
    }
    
    const blob = await response.blob()
    return { blob, filename }
  },
  
  // Health checks
  getHealth: () => api.get('/health'),
  
  // Cache management
  invalidateOverviewCache: () => api.post('/cache/invalidate/overview'),
  getCacheStats: () => api.get('/cache/stats'),
  
  // Stock overview (deduplicated to avoid double calls on mount/toggle)
  getStockOverview: (operatorCode, specialOnly = true) => {
    const url = `/operators/${operatorCode}/stock-overview?special_only=${specialOnly}`
    return apiService._dedupGet(url)
  },
  
  // Get operator products
  getOperatorProducts: (operatorCode) => {
    const url = `/operators/${operatorCode}/products`
    return apiService._dedupGet(url)
  },
  
  // Get stock+exposition summary for dashboard
  getStockExpositionSummary: (operatorCode) => {
    const url = `/operators/${operatorCode}/stock-exposition-summary`
    return api.get(url) // Not using dedup - this is async loaded
  },
  
  // Get salon attributes summary (visualization from kam_sfa attributes) for Plus/Play
  getSalonAttributesSummary: (operatorCode) => {
    const url = `/operators/${operatorCode}/salon-attributes-summary`
    return api.get(url) // Not using dedup - this is async loaded
  },
  
  // Stock special reports
  getStockSpecialReport: (operatorCode) => 
    api.get(`/operators/${operatorCode}/reports/special/plus-details`),
  
  // Plant stock products
  getPlantStockProducts: (operatorCode, plantCode) => 
    api.get(`/operators/${operatorCode}/stock/${plantCode}/products`),
  
  // Orange salon analysis
  getOrangeSalonAnalysis: (limit = 50, month = null, year = null, startDate = null, endDate = null) => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    if (startDate) params.append('start_date_str', startDate)
    if (endDate) params.append('end_date_str', endDate)
    return api.get(`/sales/orange-salon-analysis?${params}`)
  },
  
  // Orange salon analysis PDF export
  getOrangeSalonAnalysisPDF: (limit = 10000, month = null, year = null, startDate = null, endDate = null, specialAttentionOnly = false) => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    if (startDate) params.append('start_date_str', startDate)
    if (endDate) params.append('end_date_str', endDate)
    if (specialAttentionOnly) params.append('special_attention_only', 'true')
    return api.get(`/sales/orange-salon-analysis/pdf?${params}`, { responseType: 'blob' })
  },
  
  // Orange salon Pareto export
  getOrangeSalonParetoExport: (month = null, year = null) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    const queryString = params.toString()
    return api.get(`/sales/orange-salon-pareto-export${queryString ? '?' + queryString : ''}`, { responseType: 'blob' })
  },

  // Orange salon stock-exposition impact analysis
  getOrangeSalonStockExpositionImpact: (month = null, year = null, startDate = null, endDate = null, dataSource = 'operator', includeAllSalons = false) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    if (startDate) params.append('start_date_str', startDate)
    if (endDate) params.append('end_date_str', endDate)
    if (dataSource) params.append('data_source', dataSource)
    if (includeAllSalons) params.append('include_all_salons', 'true')
    return api.get(`/sales/orange-salon-stock-exposition-impact?${params}`)
  },

  // Orange districts analysis
  getOrangeDistrictsAnalysis: (limit = 100, month = null, year = null, productGroup = null) => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    if (productGroup) params.append('product_group', productGroup)
    return api.get(`/sales/orange-districts-analysis?${params}`)
  },

  // Orange districts export
  getOrangeDistrictsExport: (month = null, year = null, productGroup = null) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    if (productGroup) params.append('product_group', productGroup)
    const queryString = params.toString()
    return api.get(`/sales/orange-districts-export${queryString ? '?' + queryString : ''}`, { responseType: 'blob' })
  },

  // Orange FTE (Full-Time Employee) analysis
  getOrangeFTEAnalysis: (limit = 100, month = null, year = null, region = null, district = null) => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    if (region) params.append('region', region)
    if (district) params.append('district', district)
    return api.get(`/sales/orange-fte-analysis?${params.toString()}`)
  },

  // Orange FTE export
  getOrangeFTEExport: (month = null, year = null, region = null, district = null) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    if (region) params.append('region', region)
    if (district) params.append('district', district)
    const queryString = params.toString()
    return api.get(`/sales/orange-fte-export${queryString ? '?' + queryString : ''}`, { responseType: 'blob' })
  },

  // Plus salon analysis
  getPlusSalonAnalysis: (limit = 50, month = null, year = null, startDate = null, endDate = null) => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    return api.get(`/sales/plus-salon-analysis?${params}`)
  },
  
  // Plus salon analysis PDF export
  getPlusSalonAnalysisPDF: (limit = 10000, month = null, year = null, startDate = null, endDate = null, specialAttentionOnly = false) => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    if (specialAttentionOnly) params.append('special_attention_only', 'true')
    return api.get(`/sales/plus-salon-analysis/pdf?${params}`, { responseType: 'blob' })
  },
  
  // Plus salon Pareto export
  getPlusSalonParetoExport: (month = null, year = null) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    const queryString = params.toString()
    return api.get(`/sales/plus-salon-pareto-export${queryString ? '?' + queryString : ''}`, { responseType: 'blob' })
  },

  // Plus salon stock-exposition impact analysis
  getPlusSalonStockExpositionImpact: (month = null, year = null, startDate = null, endDate = null, dataSource = 'operator', includeAllSalons = false) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    if (dataSource) params.append('data_source', dataSource)
    if (includeAllSalons) params.append('include_all_salons', 'true')
    return api.get(`/sales/plus-salon-stock-exposition-impact?${params}`)
  },

  // Play (P4) salon analysis
  getPlaySalonAnalysis: (limit = 50, month = null, year = null, startDate = null, endDate = null) => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    return api.get(`/sales/play-salon-analysis?${params}`)
  },
  
  // Play salon analysis PDF export
  getPlaySalonAnalysisPDF: (limit = 10000, month = null, year = null, startDate = null, endDate = null, specialAttentionOnly = false) => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    if (specialAttentionOnly) params.append('special_attention_only', 'true')
    return api.get(`/sales/play-salon-analysis/pdf?${params}`, { responseType: 'blob' })
  },
  
  // Play salon Pareto export
  getPlaySalonParetoExport: (month = null, year = null) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    const queryString = params.toString()
    return api.get(`/sales/play-salon-pareto-export${queryString ? '?' + queryString : ''}`, { responseType: 'blob' })
  },
  
  // Play salon stock-exposition impact analysis
  getPlaySalonStockExpositionImpact: (month = null, year = null, startDate = null, endDate = null, dataSource = 'operator', includeAllSalons = false) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    if (dataSource) params.append('data_source', dataSource)
    if (includeAllSalons) params.append('include_all_salons', 'true')
    return api.get(`/sales/play-salon-stock-exposition-impact?${params}`)
  },

  // T-Mobile salon analysis
  getTMobileSalonAnalysis: (limit = 50, month = null, year = null) => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    return api.get(`/sales/tmobile-salon-analysis?${params}`)
  },
  
  // T-Mobile salon Pareto export
  getTMobileSalonParetoExport: (month = null, year = null) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    const queryString = params.toString()
    return api.get(`/sales/tmobile-salon-pareto-export${queryString ? '?' + queryString : ''}`, { responseType: 'blob' })
  },

  // Sales map data
  getSalesMapData: (params = {}) => {
    const searchParams = new URLSearchParams()
    if (params.operator) searchParams.append('operator', params.operator)
    if (params.year !== undefined && params.year !== null) searchParams.append('year', params.year.toString())
    if (params.month !== undefined && params.month !== null) searchParams.append('month', params.month.toString())
    
    const queryString = searchParams.toString()
    return api.get(`/sales/sales-map${queryString ? '?' + queryString : ''}`)
  },

  // AI Analysis endpoints
  analyzeOperatorWithAI: (operatorCode) => 
    api.get(`/ai/analyze/${operatorCode}`, { timeout: 300000 }),

  getDataForAIAnalysis: (operatorCode) => 
    api.get(`/ai/data-for-analysis/${operatorCode}`, { timeout: 300000 }),

  getSavedAnalyses: (operatorCode = null, limit = 20, offset = 0) => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() })
    if (operatorCode) params.append('operator_code', operatorCode)
    return api.get(`/ai/analyses?${params}`)
  },

  getSavedAnalysis: (analysisId) => 
    api.get(`/ai/analyses/${analysisId}`),

  deleteSavedAnalysis: (analysisId) => 
    api.delete(`/ai/analyses/${analysisId}`),

  updateAnalysisNotes: (analysisId, notes) => 
    api.put(`/ai/analyses/${analysisId}/notes`, { notes }),

  getAnalysesSummary: () => 
    api.get(`/ai/summary`),
  
  // Goals management endpoints (ADMIN/MANAGER only)
  getGoals: (params = {}) => api.get('/admin/goals', { params }),
  getKamList: () => api.get('/admin/goals/kams'),
  createGoal: (goalData) => api.post('/admin/goals', goalData),
  getGoal: (goalId, operatorCode) => api.get(`/admin/goals/${goalId}`, { params: { operator_code: operatorCode } }),
  updateGoal: (goalId, operatorCode, goalData) => api.put(`/admin/goals/${goalId}`, goalData, { params: { operator_code: operatorCode } }),
  deleteGoal: (goalId, operatorCode) => api.delete(`/admin/goals/${goalId}`, { params: { operator_code: operatorCode } }),
  bulkUpdateGoals: (bulkData) => api.post('/admin/goals/bulk-update', bulkData),
  
  // Days off management endpoints (ADMIN/MANAGER only)
  getDaysOff: (params = {}) => api.get('/days-off', { params }),
  createDayOff: (data) => api.post('/days-off', data),
  updateDayOff: (id, data) => api.put(`/days-off/${id}`, data),
  deleteDayOff: (id) => api.delete(`/days-off/${id}`),
  generateSundays: (month, year) => api.post('/days-off/generate-sundays', { month, year }),
  
  // Visualization configuration endpoints
  // Public endpoint - used by Overview dashboard
  getVisualizationData: () => api.get('/admin/visualization/complete'),
  
  // Admin endpoints (ADMIN/MANAGER only)
  // Operator Status
  getOperatorStatusConfigs: () => api.get('/admin/visualization/operator-status'),
  getOperatorStatusConfig: (operatorCode) => api.get(`/admin/visualization/operator-status/${operatorCode}`),
  updateOperatorStatus: (operatorCode, data) => api.put(`/admin/visualization/operator-status/${operatorCode}`, data),
  createOperatorStatus: (data) => api.post('/admin/visualization/operator-status', data),
  
  // Priority Actions
  getPriorityActions: (params = {}) => api.get('/admin/visualization/priority-actions', { params }),
  createPriorityAction: (data) => api.post('/admin/visualization/priority-actions', data),
  updatePriorityAction: (actionId, data) => api.put(`/admin/visualization/priority-actions/${actionId}`, data),
  deletePriorityAction: (actionId) => api.delete(`/admin/visualization/priority-actions/${actionId}`),
  reorderPriorityActions: (actionIds) => api.put('/admin/visualization/priority-actions/reorder', { action_ids: actionIds }),
  
  // Legend Items
  getLegendItems: (params = {}) => api.get('/admin/visualization/legend', { params }),
  createLegendItem: (data) => api.post('/admin/visualization/legend', data),
  updateLegendItem: (itemId, data) => api.put(`/admin/visualization/legend/${itemId}`, data),
  deleteLegendItem: (itemId) => api.delete(`/admin/visualization/legend/${itemId}`),
  
  // Implementation Overrides
  getImplementationOverrides: (params = {}) => api.get('/admin/visualization/implementation-overrides', { params }),
  createImplementationOverride: (data) => api.post('/admin/visualization/implementation-overrides', data),
  updateImplementationOverride: (overrideId, data) => api.put(`/admin/visualization/implementation-overrides/${overrideId}`, data),
  deleteImplementationOverride: (overrideId) => api.delete(`/admin/visualization/implementation-overrides/${overrideId}`),

  // Operator Order
  getOperatorOrder: () => api.get('/admin/visualization/operator-order'),
  updateOperatorOrder: (operatorCodes) => api.put('/admin/visualization/operator-order', { operator_codes: operatorCodes }),
  
  // History
  getVisualizationHistory: (params = {}) => api.get('/admin/visualization/history', { params }),
  getHistoryRecord: (historyId) => api.get(`/admin/visualization/history/${historyId}`),
  
  // Plus Sprzedaż
  getPlusSales: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    const qs = params.toString()
    return api.get(`/sales/plus-sprzedaz${qs ? '?' + qs : ''}`)
  },

  // Plus Agent drill-down (filter by agent / RKS / KR / pos_type / region)
  getPlusAgentDrillDown: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    return api.get(`/sales/plus-agent-drill-down?${params.toString()}`)
  },

  // Orange Agent drill-down
  getOrangeAgentDrillDown: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    return api.get(`/sales/orange-agent-drill-down?${params.toString()}`)
  },

  // T-Mobile per-Agent reports
  getTmobileAgents: ({ from, to } = {}) => {
    const params = new URLSearchParams()
    if (from) params.append('from', from)
    if (to) params.append('to', to)
    const qs = params.toString()
    return api.get(`/sales/tmobile-agents${qs ? '?' + qs : ''}`)
  },
  getTmobileAgentDrillDown: ({ agentCode, agentName, from, to } = {}) => {
    const params = new URLSearchParams()
    if (agentCode) params.append('agent_code', agentCode)
    if (agentName) params.append('agent_name', agentName)
    if (from) params.append('from', from)
    if (to) params.append('to', to)
    return api.get(`/sales/tmobile-agent-drill-down?${params.toString()}`)
  },

  // Dedicated 'Sprzedaż per Agent' (Phase 2 — baza pod e-mail reports)
  getPlusAgents: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    const qs = params.toString()
    return api.get(`/sales/plus-agents${qs ? '?' + qs : ''}`)
  },
  exportPlusAgents: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    return api.get(`/sales/plus-agents/export?${params.toString()}`, { responseType: 'blob' })
  },
  getOrangeAgents: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    const qs = params.toString()
    return api.get(`/sales/orange-agents${qs ? '?' + qs : ''}`)
  },
  exportOrangeAgents: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    return api.get(`/sales/orange-agents/export?${params.toString()}`, { responseType: 'blob' })
  },
  exportTmobileAgents: ({ from, to } = {}) => {
    const params = new URLSearchParams()
    if (from) params.append('from', from)
    if (to) params.append('to', to)
    return api.get(`/sales/tmobile-agents/export?${params.toString()}`, { responseType: 'blob' })
  },

  // Daily reports (Phase 2A — UI generation, base for emailing)
  getOrangeRegionDaily: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    const qs = params.toString()
    return api.get(`/sales/orange-region-daily${qs ? '?' + qs : ''}`)
  },
  exportOrangeRegionDaily: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    return api.get(`/sales/orange-region-daily/export?${params.toString()}`, { responseType: 'blob' })
  },
  getOrangeShelfSales: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    const qs = params.toString()
    return api.get(`/sales/orange-shelf-sales${qs ? '?' + qs : ''}`)
  },
  exportOrangeShelfSales: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    return api.get(`/sales/orange-shelf-sales/export?${params.toString()}`, { responseType: 'blob' })
  },
  getOrangeLostOpportunities: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    const qs = params.toString()
    return api.get(`/sales/orange-lost-opportunities${qs ? '?' + qs : ''}`)
  },
  exportOrangeLostOpportunities: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    return api.get(`/sales/orange-lost-opportunities/export?${params.toString()}`, { responseType: 'blob' })
  },

  // Plus region / RKS rollup + salon↔KAM mapping (2026-08)
  getPlusRegionRollup: (filters = {}) => {
    const qs = buildQuery(filters)
    return api.get(`/sales/plus-region-rollup${qs ? '?' + qs : ''}`)
  },
  exportPlusRegionRollup: (filters = {}) =>
    api.get(`/sales/plus-region-rollup/export?${buildQuery(filters)}`, { responseType: 'blob' }),
  getPlusSalonKamMapping: (filters = {}) => {
    const qs = buildQuery(filters)
    return api.get(`/sales/plus-salon-kam-mapping${qs ? '?' + qs : ''}`)
  },
  exportPlusSalonKamMapping: (filters = {}) =>
    api.get(`/sales/plus-salon-kam-mapping/export?${buildQuery(filters)}`, { responseType: 'blob' }),
  // Single file, every tab (Regiony/RKS/Salony/KAM) — replaces the old
  // two-separate-files download from the page's one "Eksport Excel" button.
  exportPlusRegionRollupAll: (filters = {}) =>
    api.get(`/sales/plus-region-rollup/export-all?${buildQuery(filters)}`, { responseType: 'blob' }),

  // E-mail report dispatch (Phase 2 — manual sends)
  getEmailSettings: () => api.get('/admin/email/settings'),
  updateEmailSettings: (payload) => api.put('/admin/email/settings', payload),
  getEmailLog: (params = {}) => api.get('/admin/email/log', { params }),
  getEmailLogHtmlUrl: (logId) =>
    `${API_BASE_URL}/admin/email/log/${logId}/html`,
  previewAgentReport: (payload) => api.post('/admin/email/preview-agent-report', payload),
  sendAgentReport: (payload) => api.post('/admin/email/send-agent-report', payload),
  previewScopeReport: (payload) => api.post('/admin/email/preview-report', payload),
  sendScopeReport: (payload) => api.post('/admin/email/send-report', payload),

  // Admin POS authorization (PLK / OPL / TMPL)
  getAdminPosList: (operator, filters = {}) => {
    const params = new URLSearchParams({ operator })
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    return api.get(`/admin/pos?${params.toString()}`)
  },
  getAdminPosDistinct: (operator) =>
    api.get(`/admin/pos/distinct?operator=${encodeURIComponent(operator)}`),
  getAdminPos: (operator, key) =>
    api.get(`/admin/pos/${operator}/${encodeURIComponent(key)}`),
  patchAdminPos: (operator, key, fields) =>
    api.patch(`/admin/pos/${operator}/${encodeURIComponent(key)}`, { fields }),

  // Plus VIP/Standard/Zero
  getPlusVipStandardZero: () => api.get('/sales/plus-vip-standard-zero'),
  getPlusVipStandardZeroExport: () => api.get('/sales/plus-vip-standard-zero/export', { responseType: 'blob' }),

  // Orange VIP/Standard/Zero
  getOrangeVipStandardZero: (polkoweOnly = false, filters = {}) => {
    const params = new URLSearchParams()
    if (polkoweOnly) params.append('polkowe_only', 'true')
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    const qs = params.toString()
    return api.get(`/sales/orange-vip-standard-zero${qs ? '?' + qs : ''}`)
  },
  getOrangeVipStandardZeroExport: (polkoweOnly = false, filters = {}) => {
    const params = new URLSearchParams()
    if (polkoweOnly) params.append('polkowe_only', 'true')
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    const qs = params.toString()
    return api.get(`/sales/orange-vip-standard-zero/export${qs ? '?' + qs : ''}`, { responseType: 'blob' })
  },

  // Orange Lokalizator VIP/Standard/Zero
  getOrangeLocatorVipStandardZero: (polkoweOnly = false) => {
    const qs = polkoweOnly ? '?polkowe_only=true' : ''
    return api.get(`/sales/orange-locator-vip-standard-zero${qs}`)
  },
  getOrangeLocatorVipStandardZeroExport: (polkoweOnly = false) => {
    const qs = polkoweOnly ? '?polkowe_only=true' : ''
    return api.get(`/sales/orange-locator-vip-standard-zero/export${qs}`, { responseType: 'blob' })
  },

  // Orange Lokalizator Device Sales (per type — Watch Goat / Watch Luna)
  getOrangeLocatorDeviceSales: (month, year, polkoweOnly = false, filters = {}) => {
    const params = new URLSearchParams()
    if (month != null) params.append('month', String(month))
    if (year != null) params.append('year', String(year))
    if (polkoweOnly) params.append('polkowe_only', 'true')
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    const qs = params.toString()
    return api.get(`/sales/orange-locator-device-sales${qs ? '?' + qs : ''}`)
  },
  getOrangeLocatorDeviceSalesExport: (month, year, polkoweOnly = false) => {
    const params = new URLSearchParams()
    if (month != null) params.append('month', String(month))
    if (year != null) params.append('year', String(year))
    if (polkoweOnly) params.append('polkowe_only', 'true')
    const qs = params.toString()
    return api.get(`/sales/orange-locator-device-sales/export${qs ? '?' + qs : ''}`, { responseType: 'blob' })
  },

  // Orange Opaska SOS
  getOrangeOpaskaKpis: (breakdown = false) => api.get('/sales/orange-opaska-kpis', { params: { breakdown } }),
  getOrangeOpaskaSalesBreakdown: (month = null, year = null) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    const qs = params.toString()
    return api.get(`/sales/orange-opaska-sales-breakdown${qs ? '?' + qs : ''}`)
  },
  getOrangeOpaskaKamHistory: (month = null, year = null) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    const qs = params.toString()
    return api.get(`/sales/orange-opaska-kam-history${qs ? '?' + qs : ''}`)
  },
  getOrangeOpaskaRawData: (limit = 100, offset = 0) => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() })
    return api.get(`/sales/orange-opaska-raw-data?${params}`)
  },
  getOrangeOpaskaExport: (month = null, year = null) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    const qs = params.toString()
    return api.get(`/sales/orange-opaska-export${qs ? '?' + qs : ''}`, { responseType: 'blob' })
  },

  // Orange Lokalizator
  getOrangeLocatorKpis: () => api.get('/sales/orange-locator-kpis'),
  getOrangeLocatorSalesBreakdown: (month = null, year = null) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    const qs = params.toString()
    return api.get(`/sales/orange-locator-sales-breakdown${qs ? '?' + qs : ''}`)
  },
  getOrangeLocatorKamHistory: (month = null, year = null) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    const qs = params.toString()
    return api.get(`/sales/orange-locator-kam-history${qs ? '?' + qs : ''}`)
  },
  getOrangeLocatorRawData: (limit = 100, offset = 0) => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() })
    return api.get(`/sales/orange-locator-raw-data?${params}`)
  },
  getOrangeLocatorExport: (month = null, year = null) => {
    const params = new URLSearchParams()
    if (month !== null) params.append('month', month.toString())
    if (year !== null) params.append('year', year.toString())
    const qs = params.toString()
    return api.get(`/sales/orange-locator-export${qs ? '?' + qs : ''}`, { responseType: 'blob' })
  },

  // Product groups
  getProductGroups: (operatorCode) => api.get(`/operators/${operatorCode}/product-groups`),

  // Goals v2 (product-group-aware, reads from "kam-sfa-dashboard".sales_goals)
  getGoalsV2: (params = {}) => api.get('/admin/goals/v2', { params }),
  createGoalV2: (goalData) => api.post('/admin/goals/v2', goalData),
  updateGoalV2: (goalId, goalData) => api.put(`/admin/goals/v2/${goalId}`, goalData),
  deleteGoalV2: (goalId) => api.delete(`/admin/goals/v2/${goalId}`),
  bulkUpdateGoalsV2: (bulkData) => api.post('/admin/goals/v2/bulk-update', bulkData),

  // Admin product groups CRUD
  getAdminProductGroups: (params = {}) => api.get('/admin/product-groups', { params }),
  createProductGroup: (data) => api.post('/admin/product-groups', data),
  updateProductGroup: (operatorCode, groupCode, data) =>
    api.put(`/admin/product-groups/${operatorCode}/${groupCode}`, data),
  deleteProductGroup: (operatorCode, groupCode) =>
    api.delete(`/admin/product-groups/${operatorCode}/${groupCode}`),

  // Admin margins
  getMargins: (params = {}) => api.get('/admin/margins', { params }),
  createMargin: (data) => api.post('/admin/margins', data),
  deleteMargin: (id) => api.delete(`/admin/margins/${id}`),

  // Admin KAM sync
  getKamSyncDiff: (operator, params = {}) =>
    api.get('/admin/kam-sync/diff', { params: { operator, ...params } }),
  applyKamSync: (data) => api.post('/admin/kam-sync/apply', data),
  getKamSyncHistory: (params = {}) => api.get('/admin/kam-sync/history', { params }),

  // Configuration
  getRules: () => api.get('/config/rules'),

  // Generic methods
  get: (url, config = {}) => api.get(url, config),
  post: (url, data = {}, config = {}) => api.post(url, data, config)
}

export default api
